import { getApiBaseUrl } from '../utils/apiConfig';
import { store } from '../store';
import { getClientAppGateHeaders } from '../utils/clientAppGate';

interface TotpStatusResponse {
  enabled: boolean;
}

interface TotpRegistrationResponse {
  qrCodeDataUri: string;
  secret: string;
  manualEntryKey: string;
}

interface StandardApiResponse<T> {
  code: number;
  message: string;
  status: string;
  data: T;
  errors?: string[];
}

class AdminTotpService {
  private getAuthHeaders(): Record<string, string> {
    const state = store.getState().auth;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Context': 'admin',
      ...getClientAppGateHeaders(),
    };

    // Add Authorization header for authenticated requests
    if (state.token && state.token !== 'SESSION_AUTH') {
      headers['Authorization'] = `Bearer ${state.token}`;
    }

    // For session auth, include credentials
    if (state.type === 'SESSION') {
      // Session auth uses cookies, no Authorization header needed
    }

    return headers;
  }

  /**
   * Get 2FA status for the current admin user
   */
  async getStatus(): Promise<TotpStatusResponse> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/profile/2fa/status`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to get 2FA status');
      }

      const result: StandardApiResponse<TotpStatusResponse> = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error getting admin 2FA status:', error);
      throw error;
    }
  }

  /**
   * Register 2FA - Initiate registration and get QR code
   */
  async register(password: string): Promise<TotpRegistrationResponse> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/profile/2fa/register`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to register 2FA');
      }

      const result: StandardApiResponse<TotpRegistrationResponse> = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error registering admin 2FA:', error);
      throw error;
    }
  }

  /**
   * Verify OTP code and enable 2FA
   */
  async verifyAndEnable(otpCode: string): Promise<void> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/profile/2fa/verify`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ code: otpCode }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to verify and enable 2FA');
      }
    } catch (error) {
      console.error('Error verifying and enabling admin 2FA:', error);
      throw error;
    }
  }

  /**
   * Re-register 2FA - For admin users who lost access to their authenticator app
   */
  async reRegister(password: string): Promise<TotpRegistrationResponse> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/profile/2fa/reregister`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to re-register 2FA');
      }

      const result: StandardApiResponse<TotpRegistrationResponse> = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error re-registering admin 2FA:', error);
      throw error;
    }
  }

  /**
   * Disable 2FA
   */
  async disable(password: string): Promise<void> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/profile/2fa/disable`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to disable 2FA');
      }
    } catch (error) {
      console.error('Error disabling admin 2FA:', error);
      throw error;
    }
  }
}

export const adminTotpService = new AdminTotpService();

